import type { TTooltipPlacement } from "hooks/useTooltip";
import type * as React from "react";

import classNames from "classnames";
import { Tooltip } from "components/ui/Tooltip";
import { type TTypographyVariant, Typography } from "components/ui/Typography";
import { useIsOverflowed } from "hooks/useIsOverflowed";
import { useRef } from "react";

import { useStyles } from "./styles";

interface IProps {
  "content": React.ReactNode;
  "data-public"?: boolean;
  "delayShow"?: number;
  "maxInlineWidth"?: number | string; // default: 100%
  "multiline"?: boolean; // default: false
  "notInteractive"?: boolean; // default: false
  "relative"?: boolean;
  "textVariant"?: TTypographyVariant;
  "tooltipClassName"?: string;
  "tooltipPlacement"?: TTooltipPlacement;
  "width"?: number | string; // default: auto
}

export const TooltipOnOverflow: FC<IProps> = ({
  className,
  content,
  "data-public": dataPublic,
  delayShow,
  maxInlineWidth = "100%",
  multiline = false,
  notInteractive = false,
  relative = false,
  textVariant = "body_reg",
  tooltipClassName,
  tooltipPlacement,
  width = "auto",
}) => {
  const classes = useStyles({ maxInlineWidth, width });
  const textRef = useRef<HTMLDivElement>(null);
  const { overflowedX, overflowedY } = useIsOverflowed(textRef);
  const overflowed = overflowedX || (overflowedY && multiline);

  return overflowed
    ? (
        <Tooltip
          className={tooltipClassName}
          content={content}
          delayShow={delayShow}
          notInteractive={notInteractive}
          placement={tooltipPlacement}
          triggerRef={textRef}
        >
          <Typography
            className={classNames(classes.container, className)}
            data-public={dataPublic}
            noWrap={!multiline}
            relative={relative}
            variant={textVariant}
          >
            {content}
          </Typography>
        </Tooltip>
      )
    : (
        <Typography
          className={classNames(classes.container, className)}
          data-public={dataPublic}
          noWrap={!multiline}
          ref={textRef}
          relative={relative}
          variant={textVariant}
        >
          {content}
        </Typography>
      );
};
